import React, { useEffect } from 'react';
import xlsx from 'json-as-xlsx';
import { read, utils } from 'xlsx';
import styled from "styled-components";
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import FilterListIcon from '@material-ui/icons/FilterList'
import {
    Table,
    Row,
    Col,
    ButtonGroup,
    Button,
    Spinner,
    Modal,
    Form,
    InputGroup,
    Card,
    Collapse,
} from "react-bootstrap";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import SelectAndSearchComponent from "../../../../components/SelectAndSearchComponent/SelectAndSearchComponent";
import CreatableSelectComponent from "../../../../components/SelectAndSearchComponent/CreatableSelectComponent";
import { IsDSWarehouseOptions, generateExportData, flattenProductData } from '../utils/helper';
import { getWarehouses, getSuppliers, getBrands } from '../../../../store/actions/dashboardAction';
import { connect } from "react-redux";
import { useStockManagement } from '../hooks/useStockManagement';
import { useAddStock } from '../hooks/useAddStock';
import { fetchStocks } from '../services/stockService';
import { getActiveFilters } from '../utils/helper';

const TableBodyWidth = styled.div`
  table tr th {
    white-space: nowrap;
    width: initial;
    scrollbar-width: none;
    text-overflow: ellipsis;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const StyledFormControl = styled(Form.Control)`
  border-radius: 0 !important;
  height: 100% !important;
`;

const InputGroupText = styled(InputGroup.Text)`
  border-radius: 0 !important;
`;

const StockList = (props) => {
    useEffect(() => {
        props.getSuppliers({});
        props.getWarehouses({});
        props.getBrands({});
    }, []);

    // Use stock management hook
    const stockManagement = useStockManagement(props);
    
    const {
        admin,
        stocks,
        page,
        totalPages,
        loading,
        error,
        uploadErrorMessage,
        inputPage,
        showFilter,
        isLoading,
        filters,
        exportFilters,
        limit,
        orderField,
        orderType,
        editedStocks,
        warehouseLocationsMap,
        showUploadStock,
        restockFile,
        isOpenModalExportReport,
        setInputPage,
        setShowFilter,
        setIsOpenModalExportReport,
        handlePageChange,
        handleFilterChange,
        handleExportFilterChange,
        applyFilters,
        handleKeyPress,
        resetFilters,
        handleEditStockChange,
        handleWarehouseLocationStockChange,
        handleQuantityBlur,
        handleShowUploadStock,
        handleChangeStockFile,
        handleSubmitUploadStock,
        handleExportReport,
    } = stockManagement;

    // Use add stock hook
    const addStockHook = useAddStock(
        filters,
        page,
        limit,
        orderField,
        orderType,
        stockManagement.setStocks,
        stockManagement.setTotalPages,
        stockManagement.setError,
        stockManagement.getWarehouseLocations
    );

    const {
        showAddRow,
        setShowAddRow,
        isLoading: isAddingStock,
        newStock,
        supplierProductNumberMap,
        supplierProductMap,
        warehouses,
        warehouseLocations,
        handleNewStockChange,
        handleAddStock,
    } = addStockHook;

    // Custom export handler with utility function
    const handleSubmitExportReport = async () => {
        const filtersToSend = getActiveFilters(exportFilters);
        fetchStocks(null, null, null, null, filtersToSend)
            .then(response => {
                if (response.list) {
                    const flattenData = flattenProductData(response.list, orderField, orderType);
                    const { columns, content } = generateExportData(flattenData, admin);
                    
                    const reportData = [
                        {
                            sheet: 'Stock Report',
                            columns: columns,
                            content: content,
                        },
                    ];
                    const settings = {
                        fileName: 'Stock_Report',
                        extraLength: 3,
                        writeMode: 'writeFile',
                        writeOptions: {},
                        RTL: false,
                    };
                    xlsx(reportData, settings);
                } else {
                    console.error(response);
                    alert("Failed to export report: " + (response.error_message || response.error));
                }
            })
            .catch(error => {
                console.error('Error exporting report:', error);
                alert("Error exporting report: " + error.message);
            }).finally(() => {
                setIsOpenModalExportReport(false);
            });
    };

    return (
        <>
            {/* Filter */}
            <Row className="mb-3">
                <Col>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowFilter(!showFilter)}
                        className="d-flex align-items-center"
                    >
                        <FilterListIcon className="mr-1" />
                        {showFilter ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                </Col>
            </Row>

            <Collapse in={showFilter}>
                <Card className="mb-3">
                    <Card.Body>
                        <Row>
                            <Col md={3} className="mb-2">
                                <Form.Group>
                                    <Form.Label>Product Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={filters.product_code}
                                        onChange={(e) => handleFilterChange('product_code', e.target.value)}
										onKeyPress={handleKeyPress}
                                        placeholder="Filter by product code"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-2">
                                <Form.Group>
                                    <Form.Label>Supplier Product Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={filters.supplier_product_name}
                                        onChange={(e) => handleFilterChange('supplier_product_name', e.target.value)}
										onKeyPress={handleKeyPress}
                                        placeholder="Filter by supplier product name"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-2">
                                <Form.Group>
                                    <Form.Label>Supplier Product Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={filters.supplier_product_number}
                                        onChange={(e) => handleFilterChange('supplier_product_number', e.target.value)}
										onKeyPress={handleKeyPress}
                                        placeholder="Filter by supplier code"
                                    />
                                </Form.Group>
                            </Col>
                            {admin && (
                                <Col md={2} className="mb-2">
                                    <Form.Group>
                                        <Form.Label>Warehouse</Form.Label>
                                        <SelectAndSearchComponent
                                            options={props.warehouses}
                                            value={filters.warehouse_name_set}
                                            onChange={(e) => handleFilterChange('warehouse_name', e, true)}
                                            placeholder="Warehouse"
                                            getOptionValue={(opt) => opt.name}
                                            getOptionLabel={(opt) => (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                    {opt.name}
                                                </div>
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            <Col md={1} className="mb-2">
                                <Form.Group>
                                    <Form.Label>Location</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={filters.location_name}
                                        onChange={(e) => handleFilterChange('location_name', e.target.value)}
										onKeyPress={handleKeyPress}
                                        placeholder="Location"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        {admin && (
                            <Row>
                                <Col md={3} className="mb-2">
                                    <Form.Group>
                                        <Form.Label>Brand</Form.Label>
                                            <SelectAndSearchComponent
                                                options={props.brands}
                                                value={filters.brand}
                                                onChange={(e) => handleFilterChange('brand', e, true)}
                                                placeholder="Brand"
                                                getOptionValue={(opt) => opt.name}
                                                getOptionLabel={(brand) => (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                        {brand.name}
                                                    </div>
                                                )}
                                            />
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="mb-2">
                                    <Form.Group>
                                        <Form.Label>Supplier</Form.Label>
                                            <SelectAndSearchComponent
                                                options={props.suppliers}
                                                value={filters.supplier}
                                                onChange={(e) => handleFilterChange('supplier', e, true)}
                                                placeholder="Supplier"
                                                getOptionValue={(opt) => opt.store_users[0].first_name}
                                                getOptionLabel={(supplier) => (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                        {supplier.store_users[0].first_name}
                                                    </div>
                                                )}
                                            />
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="mb-2">
                                    <Form.Group>
                                        <Form.Label>Quantity less than</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={filters.available_quantity}
                                            onChange={(e) => handleFilterChange('available_quantity', e.target.value)}
										onKeyPress={handleKeyPress}
                                            placeholder="Filter by quantity"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="mb-4">
                                    <Form.Group>
                                        <Form.Label>Is DS warehouse?</Form.Label>
                                        <SelectAndSearchComponent
                                            options={IsDSWarehouseOptions}
                                            value={filters.is_ds_warehouse}
                                            onChange={(e) => handleFilterChange('is_ds_warehouse', e, true)}
                                            placeholder="DS warehouse"
                                            getOptionValue={(opt) => opt.value}
                                            getOptionLabel={(opt) => (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                    {opt.key}
                                                </div>
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <Row className="mt-2">
                            <Col className="d-flex justify-content-end">
                                <Button
                                    variant="secondary"
                                    className="mr-2"
                                    onClick={resetFilters}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={applyFilters}
                                >
                                    Apply Filters
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Collapse>

            {/* Header */}
            <Row>
                <Col lg={6}>
                    <Button variant="success" onClick={handleShowUploadStock}>
                        Upload Stocks xlsx
                    </Button>
                    &nbsp;
                    <Button variant="info" className="btn-info" onClick={handleExportReport}>
                        Export
                    </Button>
                    &nbsp;
                    <Button variant="primary" onClick={() => setShowAddRow(!showAddRow)}>
                        {showAddRow ? 'Cancel' : 'Add Stock'}
                    </Button>
                </Col>
                <Col lg={6}>
                    <ButtonGroup className="pull-right">
                        <Button
                            variant="primary"
                            onClick={() => handlePageChange(1)}
                            disabled={page === 1 || loading}
                        >
                            First
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1 || loading}
                        >
                            <ArrowBackIosIcon style={{ fontSize: "18px" }} />
                        </Button>

                        <InputGroup style={{ width: 'auto' }}>
                            <StyledFormControl
                                type="number"
                                min={1}
                                max={totalPages}
                                value={inputPage || page}
                                onChange={(e) => setInputPage(parseInt(e.target.value) || '')}
                                onBlur={() => {
                                    const newPage = parseInt(inputPage);
                                    if (newPage && newPage >= 1 && newPage <= totalPages) {
                                        handlePageChange(newPage);
                                        setInputPage('');
                                    } else {
                                        setInputPage('');
                                    }
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    }
                                }}
                                disabled={loading}
                            />
                            <InputGroupText>of {totalPages}</InputGroupText>
                        </InputGroup>

                        <Button
                            variant="primary"
                            onClick={() => handlePageChange(page + 1)}
                            style={{ textAlign: 'center' }}
                            disabled={page === totalPages || loading}
                        >
                            <NavigateNextIcon style={{ fontSize: "25px" }} />
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={page === totalPages || loading}
                        >
                            Last
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>

            {error && (
                <div className="alert alert-danger mt-3">{error}</div>
            )}

            <TableBodyWidth className="table-responsive" style={{ height: "calc(100vh - 280px)", marginTop: "5px" }}>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>
                ) : (
                    <Table striped bordered hover style={{ tableLayout: "fixed", width: "100%" }}>
                        <colgroup>
                            <col style={{ width: "15%" }} />   {/* Supplier */}
                            <col style={{ width: "20%" }} />   {/* Product Name */}
                            <col style={{ width: "15%" }} />   {/* Product Code */}
                            <col style={{ width: "20%" }} />   {/* Warehouse */}
                            <col style={{ width: "15%" }} />   {/* Location */}
                            <col style={{ width: "15%" }} />   {/* Quantity */}
                        </colgroup>
                        <thead>
                            <tr>
                                <th
                                    // onClick={() => handleSortOrder("supplier")}
                                    className="sort-style"
                                >
                                    Supplier
                                </th>
                                <th
                                    // onClick={() => handleSortOrder("supplier_product_name")}
                                    className="sort-style"
                                >
                                    Supplier Product Name
                                </th>
                                <th
                                    // onClick={() => handleSortOrder("supplier_product_number")}
                                    className="sort-style"
                                >
                                    supplier product code
                                </th>
                                <th
                                    // onClick={() => handleSortOrder("warehosue")}
                                    className="sort-style"
                                >
                                    Warehouse
                                </th>
                                <th
                                    // onClick={() => handleSortOrder("location")}
                                    className="sort-style"
                                >
                                    Location
                                </th>
                                <th
                                    // onClick={() => handleSortOrder("quantity")}
                                    className="sort-style"
                                >
                                    Quantity
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {showAddRow && (
                                <tr>
                                    <td>
                                        <SelectAndSearchComponent
                                            options={props.suppliers}
                                            value={newStock.supplier}
                                            onChange={(e) => handleNewStockChange('supplier', e)}
                                            placeholder="Supplier"
                                            getOptionValue={(opt) => opt.store_users[0].first_name}
                                            getOptionLabel={(supplier) => (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                    {supplier.store_users[0].first_name}
                                                </div>
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            value={newStock.supplier_product_name}
                                            onChange={(e) => handleNewStockChange('supplier_product_name', e.target.value)}
                                            placeholder="Enter product name"
                                        />
                                    </td>
                                    <td>
                                        <CreatableSelectComponent
                                            options={supplierProductNumberMap}
                                            value={newStock.supplier_product_number}
                                            onChange={(e) => handleNewStockChange('supplier_product_number', e)}
                                            placeholder="Product Number"
                                        />
                                    </td>
                                    <td>
                                        <SelectAndSearchComponent
                                            options={warehouses}
                                            value={newStock.warehouse}
                                            onChange={(e) => handleNewStockChange('warehouse', e)}
                                            placeholder="Warehouse"
                                            getOptionValue={(opt) => opt.name}
                                            getOptionLabel={(opt) => (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                    {opt.name}
                                                </div>
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <CreatableSelectComponent
                                            options={warehouseLocations || []}
                                            value={newStock.location_name}
                                            onChange={(e) => handleNewStockChange('location_name', e)}
                                            placeholder="Location"
                                            isClearable
                                        />
                                    </td>
                                    <td>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                placeholder="00"
                                                value={newStock.available_quantity}
                                                onChange={(e) => handleNewStockChange('available_quantity', e.target.value)}
                                            />
                                            <Button
                                                variant="primary"
                                                onClick={handleAddStock}
                                                disabled={isAddingStock}
                                            >
                                                {isAddingStock ? 'Submitting...' : 'Submit'}
                                            </Button>
                                        </InputGroup>
                                    </td>
                                </tr>
                            )}
                            {stocks.length > 0 ? (
                                stocks.map((stock, key) => (
                                    <tr key={key} id={`item-row-${stock.item_id}`}>
                                        <td>{stock.supplier}</td>
                                        <td>{stock.supplier_product_name}</td>
                                        <td>{stock.supplier_product_number}</td>
                                        <td>{stock.warehouse_name}</td>
                                        {/* <td>{stock.location_name}</td> */}
                                        <td>
                                            <CreatableSelectComponent
                                                options={warehouseLocationsMap[stock.warehouse_id] || []}
                                                value={editedStocks[stock.stock_id]?.location_name ?? { value: stock.location_name, label: stock.location_name }}
                                                onChange={(e) => handleWarehouseLocationStockChange(stock.stock_id, stock.warehouse_id, e)}
                                            />
                                        </td>
	                                    <td>
		                                    <Form.Control
			                                    type="number"
			                                    placeholder="00.00"
			                                    required
			                                    value={editedStocks[stock.stock_id]?.available_quantity ?? stock.available_quantity}
			                                    onChange={(e) => handleEditStockChange(stock.stock_id, 'available_quantity', e.target.value)}
			                                    onBlur={() => handleQuantityBlur(stock.stock_id)}
		                                    />
	                                    </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center">No stocks found</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </TableBodyWidth>

            {/* Upload Stock */}
            <Modal
                show={showUploadStock}
                onHide={handleShowUploadStock}
                aria-labelledby="contained-modal-title-vcenter"
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Upload Stock</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    
                    {uploadErrorMessage && (
                        <div className="alert alert-danger mt-3">{uploadErrorMessage}</div>
                    )}
                    <Form>
                        <Form.Row>
                            <Form.Group>
                                <Form.Control
                                    type="file"
                                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                    onChange={handleChangeStockFile}
                                />
                            </Form.Group>
                            <Button
                                variant="primary"
                                type="submit"
                                className="ml-auto"
                                onClick={handleSubmitUploadStock}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <Spinner
                                        className="mr-1"
                                        as="span"
                                        animation="grow"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    ""
                                )}
                                {isLoading ? "Loading..." : "Upload"}
                            </Button>
                        </Form.Row>
                    </Form>
                    <Row className="mt-2 mb-1">
                        <Col>
                            <p className="lead">Preview(read only 50 rows)</p>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TableBodyWidth
                                className="table-responsive"
                                style={{ height: "calc(100vh - 250px)" }}
                            >
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Supplier</th>
                                            <th>Supplier Product Sku</th>
                                            <th>Warehouse</th>
                                            <th>Warehouse Location</th>
                                            {admin && <th>Supplier Product Status</th>}
                                            <th>Qty in Stock</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {restockFile !== null ? (
                                            restockFile.map((stock, index) => (
                                                <tr key={index}>
                                                    <td>{stock.supplier}</td>
                                                    <td>{stock.supplier_product_number}</td>
                                                    <td>{stock.warehouse_name}</td>
                                                    <td>{stock.location_name}</td>
                                                    {admin && <td>{stock.product_status}</td>}
                                                    <td>{stock.available_quantity}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="text-center">
                                                    No Records
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </TableBodyWidth>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer></Modal.Footer>
            </Modal>

	        {/* Modal option export xlsx report */}
            <Dialog
                open={isOpenModalExportReport}
                onClose={() => setIsOpenModalExportReport(false)}
                maxWidth="xs"
                minWidth="xs"
                fullWidth
            >
                <DialogTitle className="text-center">
                    <FilterNoneIcon /> Export Stock Report
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText id="alert-dialog-description">
                            {admin && (
                                <>
                                    <Form.Group>
                                        <Form.Label>Is DS warehouse?</Form.Label>
                                        <SelectAndSearchComponent
                                            options={IsDSWarehouseOptions}
                                            value={exportFilters.is_ds_warehouse}
                                            onChange={(e) => handleExportFilterChange('is_ds_warehouse', e)}
                                            placeholder="DS warehouse"
                                            getOptionValue={(opt) => opt.value}
                                            getOptionLabel={(opt) => (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                    {opt.key}
                                                </div>
                                            )}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Warehouse</Form.Label>
                                        <SelectAndSearchComponent
                                            options={props.warehouses}
                                            value={exportFilters.warehouse_name_set}
                                            onChange={(e) => handleExportFilterChange('warehouse_name', e)}
                                            placeholder="Warehouse"
                                            getOptionValue={(opt) => opt.name}
                                            getOptionLabel={(opt) => (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                    {opt.name}
                                                </div>
                                            )}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Supplier</Form.Label>
                                        <SelectAndSearchComponent
                                            options={props.suppliers}
                                            value={exportFilters.supplier}
                                            onChange={(e) => handleExportFilterChange('supplier', e)}
                                            placeholder="Supplier"
                                            getOptionValue={(opt) => opt.store_users[0].first_name}
                                            getOptionLabel={(supplier) => (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    {supplier.store_users[0].first_name}
                                                </div>
                                            )}
                                        />
                                    </Form.Group>
                                </>
                            )}
                            <Form.Group>
                                <Form.Label>Quantity less than</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={exportFilters.available_quantity}
                                    onChange={(e) => handleExportFilterChange('available_quantity', e.target.value)}
                                    placeholder="Filter by quantity"
                                />
                            </Form.Group>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="primary">
                        <span
                            className="pl-2 pr-2"
                            onClick={handleSubmitExportReport}
                        >
                            Export
                        </span>
                    </Button>

                    <Button variant="secondary">
                        <span
                            className="pl-2 pr-2"
                            onClick={() => setIsOpenModalExportReport(false)}
                        >
                            Dimiss
                        </span>
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        warehouses : state.dashboardReducer.warehouses,
        suppliers : state.dashboardReducer.suppliers,
        brands : state.dashboardReducer.brands,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getWarehouses: (data) => getWarehouses(data, dispatch),
        getSuppliers : data => getSuppliers(data,dispatch),
        getBrands : data => getBrands(data,dispatch),
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(StockList);